import * as React from "react";
import { useDataProvider, useNotify, useRecordContext } from "react-admin";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Checkbox,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import { useClub } from "../club/ClubContext";

type Team = { id: number; name: string };

export function AssignTeamsButton() {
  const record = useRecordContext<any>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { clubId } = useClub();

  const [open, setOpen] = React.useState(false);
  const [teams, setTeams] = React.useState<Team[]>([]);
  const [selected, setSelected] = React.useState<number[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  if (!record) return null;

 const load = async () => {
  if (!clubId) {
    notify("clubId fehlt (bitte Verein auswählen)", { type: "warning" });
    return;
  }
  setLoading(true);
  try {
    const teamsRes = await dataProvider.getList("teams", {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "name", order: "ASC" },
      filter: {}, // clubId wird bei euch global im dataProvider injiziert
    });

    // ✅ WICHTIG: linkRes definieren
    const linkRes = await dataProvider.getOne("training-templates/teams:get", {
      id: record.id,
    });

    setTeams(teamsRes.data as any);
    setSelected(linkRes.data?.team_ids ?? []);
  } catch (e: any) {
    notify(e?.message ?? "Fehler beim Laden", { type: "error" });
  } finally {
    setLoading(false);
  }
};

  const onOpen = async (e: React.MouseEvent) => {
    e.stopPropagation(); // wichtig, sonst rowClick=edit
    setOpen(true);
    await load();
  };

  const onSave = async () => {
    if (!clubId) return;
    setSaving(true);
    try {
      await dataProvider.update("training-templates/teams:set", {
  id: record.id,
  data: { team_ids: selected },
  previousData: {},
});
      notify("Teams zugeordnet.", { type: "info" });
      setOpen(false);
    } catch (e: any) {
      notify(e?.message ?? "Fehler beim Speichern", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<GroupIcon />}
        onClick={onOpen}
      >
        Teams
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Teams zuordnen</DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {loading ? (
            <CircularProgress size={22} />
          ) : (
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
              <InputLabel id="teams-label">Erlaubte Teams</InputLabel>
              <Select
                labelId="teams-label"
                multiple
                value={selected}
                onChange={(e) => setSelected(e.target.value as number[])}
                input={<OutlinedInput label="Erlaubte Teams" />}
                renderValue={(ids) => {
                  if ((ids as number[]).length === 0) return "Alle Teams (keine Einschränkung)";
                  const map = new Map(teams.map((t) => [t.id, t.name]));
                  return (ids as number[]).map((id) => map.get(id) ?? id).join(", ");
                }}
              >
                {teams.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    <Checkbox checked={selected.includes(t.id)} />
                    <ListItemText primary={t.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={onSave} variant="contained" disabled={saving || loading}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
